import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useModules, MODULE_MAP } from "@/lib/modules";
import { useFeatures } from "@/lib/features";

const NAV_FEATURE = {
  "nav-purchases": "estoque.purchases", "nav-suppliers": "estoque.suppliers", "nav-counter-sale": "estoque.counter_sale",
  "nav-reconcile": "financeiro.conciliacao", "nav-satisfaction": "crm.satisfacao", "nav-post-sale": "crm.posvenda",
  "nav-reminders": "crm.lembretes", "nav-funnel": "crm.funil", "nav-loyalty": "crm.clube",
  "nav-profit": "relatorios.rentabilidade", "nav-team": "relatorios.equipe", "nav-report-summary": "relatorios.equipe", "nav-commissions": "relatorios.equipe", "nav-vehicles-employee": "relatorios.equipe",
  "nav-dre": "relatorios.dre", "nav-tires": "relatorios.pneus", "nav-low-margin": "relatorios.margem_baixa",
  "nav-suggestions": "estoque.sugestao",
};
import { Plus, ChevronDown, LayoutGrid, ClipboardList, Columns3, CalendarDays, ShoppingCart, Users, Bell, Filter, Star, Trophy, Boxes, PackagePlus, Truck, Landmark, Handshake, Calculator, Receipt, LineChart, TrendingUp, UsersRound, Settings, Contact, Wrench, Layers, FileUp, ToggleRight, BarChart3, CircleDot, AlertTriangle, Percent, Car, TrendingDown } from "lucide-react";

const I = (to, label, icon, testid) => ({ to, label, icon, testid });
const GROUPS = [
  ["patio", "Pátio", [I("/owner", "Central", LayoutGrid, "nav-hub"), I("/owner/patio", "Pátio", Columns3, "nav-yard"), I("/owner/ordens", "Ordens", ClipboardList, "nav-orders"),
    I("/owner/agendamentos", "Agendamentos", CalendarDays, "nav-bookings"), I("/owner/balcao", "Venda balcão", ShoppingCart, "nav-counter-sale")]],
  ["clientes", "Clientes", [I("/owner/customers", "Clientes", Users, "nav-customers"), I("/owner/lembretes", "Lembretes", Bell, "nav-reminders"),
    I("/owner/funil", "Funil", Filter, "nav-funnel"), I("/owner/pos-venda", "Pós-venda", Star, "nav-post-sale"),
    I("/owner/satisfacao", "Satisfação", Star, "nav-satisfaction"), I("/owner/clube", "Clube de pontos", Trophy, "nav-loyalty")]],
  ["estoque", "Estoque", [I("/owner/estoque", "Estoque", Boxes, "nav-stock"), I("/owner/compras", "Compras", PackagePlus, "nav-purchases"),
    I("/owner/sugestao-compra", "Sugestão de compra", AlertTriangle, "nav-suggestions"),
    I("/owner/fornecedores", "Fornecedores", Truck, "nav-suppliers")]],
  ["financeiro", "Financeiro", [I("/owner/financeiro", "Financeiro", Landmark, "nav-finance"), I("/owner/dre", "DRE e balancete", BarChart3, "nav-dre"),
    I("/owner/terceirizados", "Terceirizados", Handshake, "nav-outsourced"),
    I("/owner/prejuizos", "Prejuízos", TrendingDown, "nav-losses"),
    I("/owner/calculadoras", "Calculadoras", Calculator, "nav-calculators")]],
  ["fiscal", "Fiscal", [I("/owner/notas", "Notas fiscais", Receipt, "nav-invoices")]],
  ["relatorios", "Relatórios", [I("/owner/dashboard", "Painel", LineChart, "nav-kpi"), I("/owner/rentabilidade", "Rentabilidade", TrendingUp, "nav-profit"),
    I("/owner/pneus", "Venda de pneus", CircleDot, "nav-tires"),
    I("/owner/margem-baixa", "Margem baixa", AlertTriangle, "nav-low-margin"),
    I("/owner/equipe", "Equipe (relatório)", UsersRound, "nav-team"),
    I("/owner/relatorios/resumo", "Resumo Geral", BarChart3, "nav-report-summary"),
    I("/owner/relatorios/comissoes", "Comissões", Percent, "nav-commissions"),
    I("/owner/relatorios/veiculos-funcionario", "Veículos por Funcionário", Car, "nav-vehicles-employee")]],
  ["config", "Configurações", [I("/owner/settings", "Oficina", Settings, "nav-settings"), I("/owner/funcionarios", "Funcionários (RH)", Contact, "nav-employees"),
    I("/owner/mao-de-obra", "Serviços", Wrench, "nav-labor"), I("/owner/meu-plano", "Meu plano", Layers, "nav-my-plan"), I("/owner/importar", "Importar dados", FileUp, "nav-import"), I("/owner/telas-e-funcoes", "Telas e funções", ToggleRight, "nav-features"), I("/owner/pacotes", "Pacotes adicionais", PackagePlus, "nav-addons")]],
];

const isActive = (to, path) => (to === "/owner" ? path === "/owner"
  : to === "/owner/ordens" ? path.startsWith("/owner/ordens") || path.startsWith("/owner/veiculo")
  : path === to || path.startsWith(to + "/"));

function Group({ id, label, items, path, open, setOpen }) {
  const active = items.some((it) => isActive(it.to, path));
  const isOpen = open === id;
  return (
    <div className="relative" onMouseEnter={() => setOpen(id)} onMouseLeave={() => setOpen(null)}>
      <button type="button" data-testid={`topnav-${id}`} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : id)}
        className={cn("inline-flex items-center gap-1 px-3 h-9 rounded-md text-[13px] font-semibold transition-colors",
          active ? "text-green-300 bg-green-brand/10" : "text-zinc-300 hover:text-zinc-100 hover:bg-zinc-900")}>
        {label}<ChevronDown className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
      </button>
      {isOpen && (
        <div data-testid={`topnav-${id}-menu`} className="absolute left-0 top-full pt-1 z-50">
          <div className="min-w-[13rem] rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl p-1.5 animate-in fade-in-0 zoom-in-95">
            {items.map(({ to, label: l, icon: Icon, testid }) => (
              <Link key={to} to={to} data-testid={`${testid}-top`} onClick={() => setOpen(null)}
                className={cn("flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors",
                  isActive(to, path) ? "bg-green-brand/15 text-green-300" : "text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100")}>
                <Icon className="h-4 w-4" />{l}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function OwnerTopNav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const { enabled } = useModules();
  const feats = useFeatures();
  useEffect(() => { setOpen(null); }, [pathname]);
  const groups = GROUPS.map(([id, label, items]) => [id, label, items.filter((it) => {
    const mod = MODULE_MAP[it.testid];
    const feat = NAV_FEATURE[it.testid];
    return (!mod || enabled(mod)) && (!feat || feats.enabled(feat));
  })]).filter(([, , items]) => items.length > 0);
  return (
    <nav ref={ref} data-testid="owner-topnav" className="hidden lg:block border-t border-zinc-800/70">
      <div className="px-6 h-12 flex items-center gap-1">
        <Link to="/owner/nova-ordem" data-testid="topnav-new-order"
          className="mr-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-green-brand text-white text-[13px] font-bold uppercase tracking-wide shadow-[0_0_16px_rgba(21,128,61,0.35)] hover:brightness-110 transition-[filter]">
          <Plus className="h-4 w-4" />Nova OS
        </Link>
        {groups.map(([id, label, items]) => <Group key={id} id={id} label={label} items={items} path={pathname} open={open} setOpen={setOpen} />)}
      </div>
    </nav>
  );
}
